import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X, Rocket, Zap, ChevronDown, Briefcase, Shield, Home, CalendarDays } from "lucide-react";
import { openCalendly } from "@/lib/calendly";

// Product/funding items grouped under "Funding ▾"
const products = [
  { label: "All Services", href: "#services", icon: Briefcase, blurb: "Working capital, SBA, MCA & more" },
  { label: "Lightning Funding", href: "#lightning-funding", icon: Zap, blurb: "Funded in as fast as 4 hours", accent: true },
  { label: "Credit Repair", href: "#credit-repair", icon: Shield, blurb: "Rebuild score, unlock better funding" },
  { label: "HELOC Express", href: "#heloc", icon: Home, blurb: "Tap home equity in as few as 5 days" },
];

// Top-level links (flat)
const topLinks = [
  { label: "MCA Consolidation", href: "#mca-consolidation" },
  { label: "How We Work", href: "#how-we-work" },
  { label: "About", href: "#about" },
  { label: "Testimonials", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export default function Nav({ onGetFunded }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const productsRef = useRef(null);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
    };
  }, []);

  // Close products dropdown on outside click
  useEffect(() => {
    if (!productsOpen) return;
    const onDown = (e) => {
      if (productsRef.current && !productsRef.current.contains(e.target)) {
        setProductsOpen(false);
      }
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [productsOpen]);

  return (
    <header
      data-testid="site-nav"
      style={{ top: "36px" }}
      className={`fixed left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "nav-scrolled" : "bg-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center gap-6">
        {/* Logo */}
        <a href="#top" data-testid="nav-logo" className="flex items-center gap-2 group flex-shrink-0">
          <span className="grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-fuchsia-600 shadow-lg shadow-purple-500/30 group-hover:scale-105 transition">
            <Rocket className="w-5 h-5 text-white" />
          </span>
          <span className="font-display text-white text-lg md:text-xl font-extrabold tracking-tight whitespace-nowrap">
            ROCKET<span className="text-purple-300">CAP</span>
          </span>
        </a>

        {/* Desktop nav — pushed to right with ml-auto */}
        <nav className="hidden lg:flex items-center gap-7 ml-auto whitespace-nowrap">
          {/* Funding dropdown */}
          <div ref={productsRef} className="relative">
            <button
              data-testid="nav-funding-dropdown"
              onClick={() => setProductsOpen((v) => !v)}
              onMouseEnter={() => setProductsOpen(true)}
              className="text-sm font-medium text-white/80 hover:text-white transition-colors inline-flex items-center gap-1.5"
            >
              Funding Programs
              <ChevronDown
                className={`w-3.5 h-3.5 transition-transform ${productsOpen ? "rotate-180" : ""}`}
              />
            </button>
            {productsOpen && (
              <div
                onMouseLeave={() => setProductsOpen(false)}
                className="absolute left-1/2 -translate-x-1/2 top-full mt-3 w-[380px] rounded-2xl bg-[#0b1121] border border-white/10 shadow-2xl p-2 z-50"
                data-testid="nav-funding-menu"
              >
                {products.map((p) => (
                  <a
                    key={p.href}
                    href={p.href}
                    data-testid={`nav-product-${p.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => setProductsOpen(false)}
                    className="flex items-start gap-3 rounded-xl px-3 py-3 hover:bg-white/5 transition group/item"
                  >
                    <span
                      className={`flex-shrink-0 w-10 h-10 rounded-xl grid place-items-center ${
                        p.accent
                          ? "bg-gradient-to-br from-amber-400 to-orange-500 text-white"
                          : "bg-white/5 border border-white/10 text-purple-300 group-hover/item:bg-purple-500/20 group-hover/item:text-white transition"
                      }`}
                    >
                      <p.icon className="w-4 h-4" />
                    </span>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-white flex items-center gap-1.5">
                        {p.label}
                        {p.accent && (
                          <span className="text-[9px] font-bold uppercase tracking-widest bg-amber-400/20 text-amber-300 px-1.5 py-0.5 rounded">
                            Fast
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-white/50 mt-0.5">{p.blurb}</p>
                    </div>
                  </a>
                ))}
              </div>
            )}
          </div>

          {topLinks.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="text-sm font-medium text-white/80 hover:text-white transition-colors relative after:absolute after:left-0 after:right-0 after:-bottom-1 after:h-[2px] after:origin-left after:scale-x-0 after:bg-purple-400 after:transition-transform hover:after:scale-x-100"
            >
              {l.label}
            </a>
          ))}
        </nav>

        {/* Right side */}
        <div className="flex items-center gap-3 lg:gap-4 lg:ml-4 flex-shrink-0 ml-auto lg:ml-4">
          <button
            data-testid="nav-book-call-btn"
            onClick={openCalendly}
            className="hidden xl:inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border border-white/30 hover:border-white hover:bg-white/10 text-white font-semibold px-4 py-2.5 text-sm tracking-wide transition-colors"
          >
            <CalendarDays className="w-4 h-4" /> Book a Call
          </button>
          <button
            data-testid="nav-get-funded-btn"
            onClick={onGetFunded}
            className="whitespace-nowrap rounded-full bg-gradient-to-r from-purple-500 to-fuchsia-500 hover:from-purple-600 hover:to-fuchsia-600 text-white font-semibold px-5 py-2.5 text-xs md:text-sm tracking-wide shadow-lg shadow-purple-500/40 hover:shadow-purple-500/60 transition-all hover:-translate-y-0.5"
          >
            GET FUNDED
          </button>
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden text-white p-2 -mr-2"
            onClick={() => setMobileOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-[#0b1121]/95 backdrop-blur-xl border-t border-white/10">
          <div className="px-6 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
            <button
              data-testid="nav-mobile-book-call-btn"
              onClick={() => {
                setMobileOpen(false);
                openCalendly();
              }}
              className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 text-white font-semibold py-3 mt-2"
            >
              <CalendarDays className="w-4 h-4" /> Book a Call
            </button>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mt-2 mb-1 px-1">
              Funding Programs
            </p>
            {products.map((p) => (
              <a
                key={p.href}
                href={p.href}
                onClick={() => setMobileOpen(false)}
                className="flex items-center gap-3 rounded-xl px-2 py-2.5 hover:bg-white/5"
              >
                <p.icon className={`w-4 h-4 ${p.accent ? "text-amber-300" : "text-purple-300"}`} />
                <span className="text-white/90 font-medium">{p.label}</span>
              </a>
            ))}
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/40 mt-4 mb-1 px-1">
              Company
            </p>
            {topLinks.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={() => setMobileOpen(false)}
                className="text-white/90 hover:text-white py-2.5 px-2 font-medium"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/admin/login"
              onClick={() => setMobileOpen(false)}
              className="text-white/50 hover:text-white py-2.5 px-2 text-sm uppercase tracking-widest font-semibold mt-2 border-t border-white/10 pt-4"
            >
              Admin
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
